import { takeEvery, put, all } from 'redux-saga/effects';
import { toastr } from 'react-redux-toastr';

import { loaderDecorator } from '../decorators/loader-decorator';

import * as types from '../../actions/school/types';
import * as actions from '../../actions/school';
import { setListSettings } from '../../actions/list-settings';

import { stateKeys as schoolPublicVacanciesStateKeys } from '../../constants/school-public-vacancies';
import vacancyStatuses from '../../constants/vacancy-statuses';
import httpStatusCodes from '../../constants/http-status-codes';
import listNames from '../../constants/list-names';

import { getListSettingsFromQueryParams } from '../../tools';
import apiFetcher from '../../tools/api-fetcher';
import queryStringify from '../../tools/query-stringify';
import handleError from '../../tools/error-helper';

function* getSchoolProfile({ schoolId }) {
  yield put(actions.setLoading(true, 'profile'));

  try {
    const { data } = yield apiFetcher.auth().get(`schools/${schoolId}`);

    yield put(actions.setSchoolProfile(data));
  } catch (exception) {
    handleError(exception, 'Failed to load school profile.');
  }

  yield put(actions.setLoading(false, 'profile'));
}

function* getSchoolSettings({ schoolId }) {
  yield put(actions.setSchoolSettingsLoading(true));

  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`schools/${schoolId}/account-settings`);

    yield put(actions.setSchoolSettings(data));
  } catch (exception) {
    handleError(exception, 'Failed to load school settings.');
  }

  yield put(actions.setSchoolSettingsLoading(false));
}

function* getSchoolUsers({ schoolId }) {
  yield put(actions.setLoading(true, 'users'));

  try {
    const { data } = yield apiFetcher.auth().get(`schools/${schoolId}/users`);

    yield put(actions.setSchoolUsers(data));
  } catch (exception) {
    handleError(exception, 'Failed to load list of school users.');
  }

  yield put(actions.setLoading(false, 'users'));
}

function* getSchoolQuestions({ schoolId, questionsType }) {
  yield put(actions.setLoading(true, questionsType));

  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`schools/${schoolId}/questions-set/${questionsType}`);

    yield put(actions.setSchoolQuestions(data, questionsType));
  } catch (exception) {
    handleError(exception, 'Failed to load school questions.');
  }

  yield put(actions.setLoading(false, questionsType));
}

function* addSchoolQuestion({ schoolId, questionType, question }) {
  yield put(actions.setLoading(true, 'questions'));
  yield put(actions.setQuestionAdding(true, questionType));

  try {
    yield apiFetcher
      .auth()
      .post(`schools/${schoolId}/questions-set/${questionType}`, question);

    yield put(actions.getSchoolQuestions(schoolId, questionType));

    toastr.success('The new question was successfully added.');
  } catch (exception) {
    handleError(exception, 'Failed to add new question.');
  }

  yield put(actions.setLoading(false, 'questions'));
  yield put(actions.setQuestionAdding(false, questionType));
}

function* removeSchoolQuestion({ schoolId, questionType, questionId }) {
  yield put(actions.setLoading(true, 'questions'));
  yield put(actions.setQuestionDeleting(true, questionType));

  try {
    yield apiFetcher
      .auth()
      .delete(
        `schools/${schoolId}/questions-set/${questionType}/${questionId}`
      );

    yield put(actions.getSchoolQuestions(schoolId, questionType));

    toastr.success('The question was successfully deleted.');
  } catch (exception) {
    handleError(exception, 'Failed to delete question.');
  }

  yield put(actions.setLoading(false, 'questions'));
  yield put(actions.setQuestionDeleting(false, questionType));
}

function* getSchoolVacancies({ queryParams }) {
  yield put(actions.setLoading(true, 'vacancies'));

  yield put(actions.setSchoolVacanciesQueryParams(queryParams));

  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`schools/vacancies?${queryStringify({ ...queryParams })}`);

    yield put(actions.setSchoolVacancies(data));

    const queryParamsWithFullVacancyStatusesSet = {
      ...queryParams,
      statuses: Object.values(vacancyStatuses)
    };

    const statusStats = yield apiFetcher.auth().get(
      `schools/vacancies/status-stats?${queryStringify({
        ...queryParamsWithFullVacancyStatusesSet
      })}`
    );

    yield put(actions.setSchoolVacanciesStatusStats(statusStats.data));

    const listSettings = yield getListSettingsFromQueryParams(queryParams);

    yield put(setListSettings(listNames.schoolVacancies, listSettings));
  } catch (exception) {
    handleError(exception, 'Failed to load vacancies.');
  }

  yield put(actions.setLoading(false, 'vacancies'));
}

function* getSchoolPublicProfile({ schoolId }) {
  try {
    const { data } = yield apiFetcher.get(`public/schools/${schoolId}/profile`);

    yield put(actions.setSchoolPublicProfile(data));
  } catch (exception) {
    switch (exception.response && exception.response.status) {
      case httpStatusCodes.NotFound:
        yield put(
          actions.setSchoolPublicProfileFailedCode(exception.response.status)
        );
        break;
      default:
        handleError(exception);
        break;
    }
  }
}

function* getSchoolPublicVacancies({ schoolId }) {
  try {
    const { data } = yield apiFetcher.get(
      `public/schools/${schoolId}/vacancies`
    );

    yield put(actions.setSchoolPublicVacancies(data));
  } catch (exception) {
    switch (exception.response && exception.response.status) {
      case httpStatusCodes.NotFound:
        yield put(
          actions.setSchoolPublicVacanciesFailedCode(exception.response.status)
        );
        break;
      default:
        handleError(exception);
        break;
    }
  }
}

function* fetchSchoolPublicVacancyDetails({ schoolId, vacancyId }) {
  try {
    const { data } = yield apiFetcher.get(
      `public/schools/${schoolId}/vacancies/${vacancyId}`
    );

    yield put(actions.setSchoolPublicVacancyDetails(data));
  } catch (exception) {
    switch (exception.response && exception.response.status) {
      case httpStatusCodes.NotFound:
      case httpStatusCodes.Gone:
      case httpStatusCodes.Locked:
        yield put(
          actions.setSchoolPublicVacancyDetailsFailedCode(
            exception.response.status
          )
        );
        break;
      default:
        yield put(
          actions.setSchoolPublicVacancyDetailsFailedCode(
            exception.response.status
          )
        );
        handleError(exception);
        break;
    }
  }
}

function* getSchoolPriorCandidates({ schoolId, queryParams }) {
  yield put(actions.setSchoolPriorCandidatesLoading(true));

  try {
    const { data } = yield apiFetcher.auth().get(
      `schools/${schoolId}/candidates/prior?${queryStringify({
        ...queryParams
      })}`
    );

    yield put(actions.setSchoolPriorCandidates(data));
    yield put(actions.setPriorCandidatesQueryParams(queryParams));

    const listSettings = yield getListSettingsFromQueryParams(queryParams);

    yield put(
      setListSettings(listNames.schoolVacancyPriorCandidates, listSettings)
    );
  } catch (exception) {
    handleError(exception, 'Failed to load prior candidates.');
  }

  yield put(actions.setSchoolPriorCandidatesLoading(false));
}

function* updateSchoolSettings({ schoolId, settings }) {
  yield put(actions.setSchoolSettingUpdating(true));

  try {
    yield apiFetcher
      .auth()
      .put(`schools/${schoolId}/account-settings`, settings);

    yield put(actions.setSchoolSettings(settings));
  } catch (exception) {
    handleError(exception, 'Failed to load school settings.');
  }

  yield put(actions.setSchoolSettingUpdating(false));
}

function functionCallLoadingDecorator(targetFunction, loadingKey) {
  return function*() {
    yield put(actions.setLoading(true, loadingKey));

    try {
      yield targetFunction.apply(this, arguments);
    } catch (exception) {
      handleError(exception);
    }

    yield put(actions.setLoading(false, loadingKey));
  };
}

function takeEveryWithLoadingKey(type, targetFunction, loadingKey) {
  return takeEvery(
    type,
    functionCallLoadingDecorator(targetFunction, loadingKey)
  );
}

function takeEveryWithLoading(type, targetFunction, loader) {
  return takeEvery(type, loaderDecorator(targetFunction, loader));
}

export default function* schoolSaga() {
  yield all([
    takeEvery(types.SCHOOL_GET_PROFILE, getSchoolProfile),
    takeEvery(types.SCHOOL_GET_QUESTIONS, getSchoolQuestions),
    takeEvery(types.SCHOOL_GET_SETTINGS, getSchoolSettings),
    takeEvery(types.SCHOOL_GET_USERS, getSchoolUsers),
    takeEvery(types.SCHOOL_ADD_QUESTION, addSchoolQuestion),
    takeEvery(types.SCHOOL_REMOVE_QUESTION, removeSchoolQuestion),
    takeEvery(types.SCHOOL_GET_VACANCIES, getSchoolVacancies),
    takeEvery(types.SCHOOL_GET_PRIOR_CANDIDATES, getSchoolPriorCandidates),
    takeEvery(types.SCHOOL_UPDATE_SETTINGS, updateSchoolSettings),

    takeEveryWithLoadingKey(
      types.SCHOOL_GET_PUBLIC_PROFILE,
      getSchoolPublicProfile,
      schoolPublicVacanciesStateKeys.schoolPublicProfileLoading
    ),
    takeEveryWithLoadingKey(
      types.SCHOOL_GET_PUBLIC_VACANCIES,
      getSchoolPublicVacancies,
      schoolPublicVacanciesStateKeys.schoolPublicVacanciesLoading
    ),
    takeEveryWithLoading(
      types.SCHOOL_FETCH_PUBLIC_VACANCY_DETAILS,
      fetchSchoolPublicVacancyDetails,
      actions.setSchoolPublicVacancyDetailsLoading
    )
  ]);
}



// WEBPACK FOOTER //
// ./src/sagas/school/school.js